// @file image processing utils

import { browserCan } from '@@/bits/browser'
import { captureException } from '@@/bits/error_tracker'
import { memoize } from 'es-toolkit'

/**
 * Extracts the first frame of a GIF image and returns it as a PNG data URL.
 * This is useful for showing a static preview of GIF images when they are paused
 * or when animated content should not be shown (e.g., reduced motion mode).
 *
 * @param url - The URL of the GIF image to extract the first frame from
 * @returns A Promise that resolves to a data URL containing the first frame as a PNG,
 *          or null if the extraction fails
 *
 * @example
 * ```typescript
 * const firstFrame = await extractGifFirstFrame('https://example.com/animation.gif')
 * if (firstFrame) {
 *   // Use the data URL in an img src attribute
 *   imageElement.src = firstFrame
 * }
 * ```
 */

async function _extractGifFirstFrameCore(url: string): Promise<string> {
  let dataUrl = ''
  try {
    const response = await fetch(url)
    const blob = await response.blob()

    // Create a canvas to draw the first frame
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx == null) throw new Error('Could not get canvas context')

    if (browserCan('createImageBitmap')) {
      // Modern browsers: use createImageBitmap API
      const bitmap: ImageBitmap = await createImageBitmap(blob)

      canvas.width = bitmap.width
      canvas.height = bitmap.height

      // Draw the first frame
      ctx.drawImage(bitmap, 0, 0)

      // Convert to data URL
      dataUrl = canvas.toDataURL('image/png')

      // Cleanup
      bitmap.close()
    } else {
      // Fallback for older browsers: use Image element
      const imageElement = new Image()
      const objectUrl = URL.createObjectURL(blob)

      try {
        await new Promise<void>((resolve, reject) => {
          imageElement.onload = () => resolve()
          imageElement.onerror = () => reject(new Error('Failed to load image'))
          imageElement.src = objectUrl
        })

        canvas.width = imageElement.naturalWidth
        canvas.height = imageElement.naturalHeight

        // Draw the first frame
        ctx.drawImage(imageElement, 0, 0)

        // Convert to data URL
        dataUrl = canvas.toDataURL('image/png')
      } catch (error) {
        captureException(error)
        throw error // Re-throw to be caught by outer try-catch
      } finally {
        // Cleanup
        URL.revokeObjectURL(objectUrl)
      }
    }

    return dataUrl
  } catch (error) {
    captureException(error)
  }
  return dataUrl
}

const memoizedExtractGifFirstFrame = memoize(_extractGifFirstFrameCore)

async function extractGifFirstFrame(url: string): Promise<string | null> {
  try {
    return await memoizedExtractGifFirstFrame(url)
  } catch (error) {
    captureException(error)
    return null
  }
}

/**
 * Check if an image URL is a GIF by examining the file extension
 * @param url - The image URL to check
 * @returns true if the URL appears to be a GIF, false otherwise
 */
function isGifUrl(url: string | null | undefined): boolean {
  if (!url) return false
  return url.toLowerCase().endsWith('.gif')
}
export { extractGifFirstFrame, isGifUrl }
